import React from 'react';
import propTypes from 'prop-types';
import debounce from 'lodash/debounce';

// ChecklistBox is the one option control of the export modal: a select-looking
// box that opens a checklist. Static lists filter client side; long lists load
// through loadOptions(search) from the admin endpoints the CMS already calls.
// value is an array of { id, name }.
// MAX_RENDERED bounds the checkbox rows mounted at once; long lists are
// narrowed by typing, not scrolled.
const MAX_RENDERED = 200;

export default class ChecklistBox extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false, search: '', options: props.options || [], loading: false, failed: false };
    this.ref = React.createRef();
    this._request = 0;
    this._unmounted = false;
    this._memo = new Map(); // search term -> options, for this box's lifetime
    this._onDocClick = this._onDocClick.bind(this);
    this._onKey = this._onKey.bind(this);
    this._toggle = this._toggle.bind(this);
    this._load = debounce(this._load.bind(this), 300);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.options !== this.props.options && this.props.options) {
      this.setState({ options: this.props.options });
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    this._load.cancel();
    document.removeEventListener('mousedown', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  _onDocClick(e) {
    if (this.ref.current && !this.ref.current.contains(e.target)) this._close();
  }

  _onKey(e) {
    if (e.key === 'Escape') this._close();
  }

  _open() {
    document.addEventListener('mousedown', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
    this.setState({ open: true, search: '' });
    if (this.props.loadOptions) this._load('');
  }

  _close() {
    document.removeEventListener('mousedown', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    this.setState({ open: false });
  }

  _toggle() {
    if (this.props.disabled) return;
    if (this.state.open) this._close(); else this._open();
  }

  _load(search) {
    const request = ++this._request;
    const term = search.trim().toLowerCase();
    if (this._memo.has(term)) {
      this.setState({ options: this._memo.get(term), loading: false, failed: false });
      return;
    }
    this.setState({ loading: true, failed: false });
    this.props.loadOptions(search)
      .then(options => {
        if (this._unmounted) return;
        this._memo.set(term, options);
        if (request !== this._request) return;
        this.setState({ options, loading: false });
      })
      .catch(err => {
        if (this._unmounted || request !== this._request) return;
        console.error('ChecklistBox options failed', this.props.id, err);
        this.setState({ options: [], loading: false, failed: true });
      });
  }

  _onSearch(e) {
    const search = e.target.value;
    this.setState({ search });
    if (this.props.loadOptions) {
      this._request += 1; // a response to the previous term is stale from now on, debounce or not
      this._load(search);
    }
  }

  // value is always read through this: a preset or caller may still hand over
  // a scalar, which must not crash the modal.
  _value() {
    return Array.isArray(this.props.value) ? this.props.value : [];
  }

  _isOn(opt) {
    return this._value().some(v => String(v.id) === String(opt.id));
  }

  _toggleOpt(opt) {
    const value = this._value();
    this.props.onChange(this._isOn(opt) ? value.filter(v => String(v.id) !== String(opt.id)) : [...value, opt]);
  }

  _visible() {
    const { options, search } = this.state;
    if (this.props.loadOptions || !search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(o => String(o.name).toLowerCase().includes(q));
  }

  render() {
    const { id, placeholder, disabled, searchable, onChange } = this.props;
    const value = this._value();
    const { open, search, loading, failed } = this.state;
    const all = this._visible();
    const visible = all.slice(0, MAX_RENDERED);
    const summary = value.length ? value.map(v => v.name).join(', ') : placeholder;
    return (
      <div ref={this.ref} style={{ position: 'relative' }}>
        <button type="button" id={id} className="form-control form-control-sm d-flex align-items-center" aria-haspopup="listbox" aria-expanded={open} disabled={disabled}
          onClick={this._toggle}
          style={{ textAlign: 'left', height: 'auto', minHeight: '31px', paddingRight: '26px', borderColor: open ? '#20a8d8' : undefined, boxShadow: open ? '0 0 0 1px #20a8d8' : 'none',
            background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2373818f'/%3E%3C/svg%3E") no-repeat right 9px center` }}>
          <span className={value.length ? '' : 'text-muted'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{summary}</span>
          {value.length > 0 && <span className="badge badge-info ml-2" style={{ fontSize: '.66rem' }}>{value.length}</span>}
        </button>
        {open && (
          <div role="listbox" aria-multiselectable="true" style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 1060, background: '#fff', border: '1px solid #c2cfd6', borderTop: 0, borderRadius: '0 0 3px 3px', boxShadow: '0 8px 20px rgba(0,0,0,.12)', maxHeight: '260px', display: 'flex', flexDirection: 'column' }}>
            {searchable && (
              <input className="form-control form-control-sm border-0 rounded-0" style={{ borderBottom: '1px solid #e4e7ea' }} placeholder="Search…" aria-label={`Search ${placeholder}`} value={search} autoFocus onChange={e => this._onSearch(e)} />
            )}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loading && <div className="text-muted px-2 py-1" style={{ fontSize: '.74rem' }}>loading…</div>}
              {failed && (
                <div className="text-danger px-2 py-1" style={{ fontSize: '.74rem' }}>
                  could not load options{' '}
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" style={{ fontSize: '.74rem' }} onClick={() => this._load(search)}>retry</button>
                </div>
              )}
              {!loading && !failed && visible.length === 0 && <div className="text-muted px-2 py-1" style={{ fontSize: '.74rem' }}>no match</div>}
              {all.length > MAX_RENDERED && <div className="text-muted px-2 py-1" style={{ fontSize: '.72rem' }}>showing the first {MAX_RENDERED} of {all.length}; type to narrow</div>}
              {visible.map(opt => (
                <label key={opt.id} className="d-flex align-items-center mb-0 px-2 py-1" style={{ gap: '8px', fontSize: '.78rem', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ position: 'static', margin: 0, flexShrink: 0 }} checked={this._isOn(opt)} onChange={() => this._toggleOpt(opt)} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.name}</span>
                </label>
              ))}
            </div>
            <div className="d-flex justify-content-between px-2 py-1" style={{ borderTop: '1px solid #e4e7ea', fontSize: '.72rem' }}>
              <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.72rem' }} disabled={all.length === 0}
                onClick={() => onChange(all.reduce((acc, o) => (acc.some(v => String(v.id) === String(o.id)) ? acc : [...acc, o]), value))}>Select all{all.length > MAX_RENDERED ? ` (${all.length})` : ''}</button>
              <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.72rem' }} disabled={value.length === 0} onClick={() => onChange([])}>Clear</button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

ChecklistBox.propTypes = {
  id: propTypes.string,
  value: propTypes.arrayOf(propTypes.shape({ id: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired, name: propTypes.string.isRequired })).isRequired,
  onChange: propTypes.func.isRequired,
  options: propTypes.array,
  loadOptions: propTypes.func,
  placeholder: propTypes.string,
  disabled: propTypes.bool,
  searchable: propTypes.bool
};

ChecklistBox.defaultProps = { placeholder: 'All', disabled: false, searchable: false };

// Two-option boxes: ticking both or none means "All"; only one tick narrows.
export const singleOf = (value, options) => {
  const picked = (value || []).map(v => String(v.id));
  const on = options.filter(o => picked.includes(String(o.id)));
  return on.length === 1 ? String(on[0].id) : '';
};

export const idsOf = value => (Array.isArray(value) ? value.map(o => o.id).filter(v => v != null).join(',') : '');
